<template>
  <div id="main" class="container is-fluid">
    <div class="columns is-multiline">
      <div class="column is-5-widescreen is-5-fullhd is-12">
        <h1 class="title">{{ $t("title") }}</h1>
        <h2 class="subtitle">{{ $t("subtitle") }}</h2>
        <div id="mode-buttons">
          <button class="button is-large" :class="{'is-primary': mode === 'QR'}" @click="changeMode('QR')">
            <span class="icon is-medium">
              <i class="fa fa-qrcode"></i>
            </span>
            <span>QR Code</span>
          </button>
          <button class="button is-large" :class="{'is-primary': mode === 'Spotify'}" @click="changeMode('Spotify')">
            <span class="icon is-medium">
              <i class="fab fa-spotify"></i>
            </span>
            <span>Spotify Code</span>
          </button>
          <button class="button is-large" :class="{'is-primary': mode === 'Text'}" @click="changeMode('Text')">
            <span class="icon is-medium">
              <i class="fa fa-font"></i>
            </span>
            <span>Text</span>
          </button>
        </div>
        <hr />
        <!-- Menus for modes -->
        <QRCodeMenu v-if="mode === 'QR'" ref="qrcode" :scene="scene" :exporter="exporter" :initData="shareData" @generating="isGenerating = true" @exportReady="exportReady" @resetScene="resetScene"/>
        <SpotifyMenu v-if="mode === 'Spotify'" ref="spotifycode" :scene="scene" :exporter="exporter" :initData="shareData" @generating="isGenerating = true" @exportReady="exportReady" @resetScene="resetScene"/>
        <TextMenu v-if="mode === 'Text'" ref="text" :scene="scene" :exporter="exporter" :initData="shareData" @generating="isGenerating = true" @exportReady="exportReady" @resetScene="resetScene"/>

      </div>
      <div class="column is-7-widescreen is-7-fullhd is-12">
        <div class="columns">
          <div class="column">
            <p class="title">{{$t('preview')}}</p>
            <p class="subtitle">{{ $t("controlsHint") }}</p>
          </div>
          <div class="column is-2" v-if="showExport">
            <div class="field">
              <div class="field-label is-normal has-text-left">
                <label class="label" :title="$t('exportTypeHelp')">
                  STL {{ $t('file') }}
                  <span class="help-icon icon has-text-info"><i class="fas fa-info-circle"></i></span>
                </label>

              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    <div class="select">
                      <select v-model="stlType">
                        <option>binary</option>
                        <option>ASCII</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="column is-3" v-if="showExport">
            <div class="field">
              <div class="field-label is-normal has-text-left">
                <label class="label" :title="$t('exportSeparatePartsHelp')">
                  {{$t('separateParts')}}?
                  <span class="help-icon icon has-text-info"><i class="fas fa-info-circle"></i></span>
                </label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    <div class="select">
                      <select v-model="multipleParts">
                        <option v-bind:value="false">{{$t('no')}}</option>
                        <option v-bind:value="true">{{$t('yes')}}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="column is-3" style="padding-top: 2rem" v-if="showExport">
            <button class="button export-button is-primary is-medium" @click="exportSTL">
              <span class="icon">
                <i class="fa fa-download"></i>
              </span>
              <span>{{$t('saveAsButton')}}</span>
            </button>
          </div>
        </div>
        <hr />
        <div v-if="isGenerating" class="has-text-centered">
          <p class="title">{{$t('isGenerating')}}</p>
          <hr>
        </div>
        <div id="container3d" :class="{ 'is-loading': isGenerating }"></div>
        <p class="mt-3">
          <a class="title is-4" href="#printguide"><i class="fa fa-angle-double-down"></i> {{$t('scrollDownForGuide')}}</a>
        </p>
        <div v-html="modelAd" class="mt-4 has-text-centered"></div>
        <Promotions />
      </div>
    </div>

    <PrintGuide />
    <div class="content container">
      <h2 class="title">Changelog</h2>
      <hr>
      <vue-markdown :source="changelog" class="content"></vue-markdown>
    </div>
    <ChangelogModal v-if="changelogModalVisible"/>
    <ExportModal v-if="exportModalVisible"/>
  </div>
</template>

<script>
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter';
import VueMarkdown from 'vue-markdown';
// eslint-disable-next-line import/no-webpack-loader-syntax
import changelog from 'raw-loader!../../CHANGELOG.md';
import ChangelogModal from './ChangelogModal.vue';
import ExportModal from './ExportModal.vue';
import { bus } from '../main';
import QRCodeMenu from './QRCodeMenu.vue';
import SpotifyMenu from './SpotifyMenu.vue';
import TextMenu from './TextMenu.vue';
import PrintGuide from './PrintGuide.vue';
import Promotions from './Promotions.vue';
import { getRandomBanner } from '../utils';

const shareHashMarker = '#share';

export default {
  name: 'Main',
  props: {
    msg: String,
  },
  components: {
    QRCodeMenu,
    SpotifyMenu,
    TextMenu,
    PrintGuide,
    ChangelogModal,
    ExportModal,
    VueMarkdown,
    Promotions,
  },
  data() {
    return {
      mode: 'QR',
      showExport: true,
      stlType: 'binary',
      multipleParts: false,
      changelogModalVisible: false,
      changelog: changelog.split('\n').slice(3).join('\n'),
      exportModalVisible: false,
      shareData: null,
      isGenerating: false,
      modelAd: '',
      exporter: null,
      camera: null,
      scene: null,
      renderer: null,
      animationFrameId: null,
      animationTimer: null,
      adblockEnabled: false,
    };
  },
  created() {
    bus.$on('openChangelogModal', () => { this.changelogModalVisible = true; });
    bus.$on('closeChangelogModal', () => { this.changelogModalVisible = false; });
    bus.$on('openExportModal', () => { this.exportModalVisible = true; });
    bus.$on('closeExportModal', () => { this.exportModalVisible = false; });

    this.parseUrlShareHash();
  },
  mounted() {
    // eslint-disable-next-line camelcase
    if (typeof __google_ad_urls === 'undefined') {
      this.adblockEnabled = true;
      this.modelAd = getRandomBanner('728x90');
    } else {
      this.modelAd = document.getElementById('adsenseloader-model').innerHTML;
    }
    this.initScene();
    this.startAnimation();
    this.exporter = new STLExporter();
  },
  methods: {
    changeMode(mode) {
      window.location.hash = '';
      this.shareData = null;
      this.mode = mode;
    },
    initLights() {
      // LIGHTS
      const ambientLight = new THREE.AmbientLight(0x333333);
      this.scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xFFFFFF, 1.0);
      directionalLight.position.x = -1;
      directionalLight.position.y = 0;
      directionalLight.position.z = 1;
      this.scene.add(directionalLight);

      const directionalLightBack = new THREE.DirectionalLight(0xaaaaaa, 0.3);
      directionalLightBack.position.x = -0.6;
      directionalLightBack.position.y = 0;
      directionalLightBack.position.z = -1;
      this.scene.add(directionalLightBack);
    },
    initScene() {
      clearTimeout(this.animationTimer);
      cancelAnimationFrame(this.animationFrameId);
      this.scene = null;
      this.mesh = null;
      this.camera = null;
      this.scene = null;
      this.renderer = null;
      const elem = document.getElementById('container3d');
      while (elem.lastChild) elem.removeChild(elem.lastChild);

      const container = document.getElementById('container3d');

      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0xa0a0a0);
      this.scene.rotation.z = -Math.PI / 2;

      this.initLights();

      const grid = new THREE.GridHelper(1000, 100, 0x000000, 0x000000);
      grid.material.opacity = 0.2;
      grid.material.transparent = true;
      grid.rotation.x = Math.PI / 2;
      this.scene.add(grid);

      this.camera = new THREE.PerspectiveCamera(
        50,
        container.clientWidth / container.clientHeight,
        1,
        1000,
      );
      this.camera.position.set(0, 0, 200);

      this.renderer = new THREE.WebGLRenderer({ antialias: true });
      this.renderer.setSize(container.clientWidth, container.clientHeight);
      container.appendChild(this.renderer.domElement);
      const controls = new OrbitControls(this.camera, this.renderer.domElement);
      controls.target.set(0, 0, 0);
      controls.update();
    },
    resetScene() {
      while (this.scene.children.length > 0) {
        this.scene.remove(this.scene.children[0]);
      }

      this.initLights();

      const grid = new THREE.GridHelper(1000, 100, 0x000000, 0x000000);
      grid.material.opacity = 0.2;
      grid.material.transparent = true;
      grid.rotation.x = Math.PI / 2;
      this.scene.add(grid);
    },
    startAnimation() {
      const animate = () => {
        // limit animation to 60 FPS
        this.animationTimer = setTimeout(() => {
          this.animationFrameId = requestAnimationFrame(animate);
        }, 1000 / 60);
        this.renderer.render(this.scene, this.camera);
      };
      animate();
    },
    exportSTL() {
      this.exportModalVisible = true;
      setTimeout(() => {
        if (this.mode === 'QR') {
          this.$refs.qrcode.exportSTL(this.stlType, this.multipleParts);
        } else if (this.mode === 'Spotify') {
          this.$refs.spotifycode.exportSTL(this.stlType, this.multipleParts);
        } else if (this.mode === 'Text') {
          this.$refs.text.exportSTL(this.stlType, this.multipleParts);
        }
      }, 5000);
    },
    parseUrlShareHash() {
      if (window.location.hash.startsWith(shareHashMarker)) {
        const rawShareData = window.location.hash.substring(shareHashMarker.length).split('-');
        // eslint-disable-next-line prefer-destructuring
        const mode = rawShareData[0];
        if (mode !== 'Spotify' && mode !== 'QR' && mode !== 'Text') {
          return;
        }
        this.mode = mode;
        try {
          this.shareData = Object.assign(JSON.parse(atob(rawShareData[1])), { mode });
        } catch (error) {
          this.shareData = null;
          console.error('Invalid Sharing URL');
          window.location.hash = '';
        }
      }
    },
    exportReady(options) {
      this.showExport = true;
      try {
        window.location.hash = `${shareHashMarker}${this.mode}-${btoa(JSON.stringify(options))}`;
      } catch (error) {
        console.error(error);
      } finally {
        this.isGenerating = false;
      }
      bus.$emit('exportReady');
    },
  },

};
</script>

<style>
#main {
  margin-top: 20px;
  padding-bottom: 20px;
}

#container3d {
  width: 100%;
  height: 600px;
  border-radius: 5px;
  overflow: hidden;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  opacity: 1;
}

#container3d.is-loading {
  animation: breathing 2s linear infinite;
}

@keyframes breathing {
  0% {
    opacity: 0.3;
  }

  50% {
    opacity: 1;
  }

  100% {
    opacity: 0.3;
  }
}

.export-button {
  margin: 0 10px;
}

#notifications {
  margin-top: 10px;
}

.field-label {
  text-align: left !important;
}

#mode-buttons>button {
  margin-right: 20px;
}

.highlight {
  position: relative;
  display: inline-block;
  overflow: visible;
}

.highlight>.highlight-text {
  position: absolute;
  top: -10px;
  right: -10px;
  padding: 0px 5px;
  background-color: hsl(348, 100%, 61%);
  color: #fff;
  font-weight: bold;
  border-radius: 3px;
  z-index: 30;
}

.subsection {
  border-radius: 6px;
  background: #fbfbfb;
  border: 1px solid #ededed;
  padding: 1rem;
  margin-bottom: 1rem;
}
</style>
